import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { handleCatchedError } from '@/helpers/common'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsStore } from './assets/index'
import { useAssetsDeletedStore } from '@/store/assets/deleted'
import { useAssetPricesStore } from '@/store/assets/prices'
import { useAssetTaxLotsStore } from '@/store/assets/taxLots'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useCashFlowProjectionsStore } from '@/store/cashflow-projections'
import { useCashFlowProjectionsBunchStore } from '@/store/cashflow-projections/bunch'
import { useContactsStore } from '@/store/contacts/'
import { useContactsBunchStore } from '@/store/contacts/bunch'
import { useDashboardsStore } from '@/store/dashboards'
import { useDocumentsStore } from '@/store/documents/index'
import { useDocumentsBunchStore } from '@/store/documents/bunch'
import { useDocumentsTagsStore } from '@/store/documents/tags'
import { useFilterStore } from './filter'
import { useLinkedDataStore } from '@/store/linkedData'
import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'
import { useLinkedDataTaggingRulesStore } from './linkedData/rules/tagging'
import { useLinkedDataMappingRulesStore } from './linkedData/rules/mapping'
import { useLinkedDataTransactionsTagsStore } from '@/store/linkedData/transactionsTags'
import { useModalsStore } from './modals'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useRepositoriesCollaboratorsStore } from '@/store/repositories/collaborators'
import { useTagsBunchStore } from './tags/bunch'
import { useTagsStore } from '@/store/tags'
import { useTasksStore } from '@/store/tasks'
import { useTransactionsStore } from '@/store/transactions'
import { useTransactionsBunchStore } from '@/store/transactions/bunch'
import { useTransactionsDeletedStore } from '@/store/transactions/deleted'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'
import { useUserSettingsStore } from '@/store/user/settings'

export const useMainStore = defineStore('main', () => {
  const analyticsStore = useAnalyticsStore()
  const assetsStore = useAssetsStore()
  const assetsDeletedStore = useAssetsDeletedStore()
  const assetPricesStore = useAssetPricesStore()
  const assetTaxLotsStore = useAssetTaxLotsStore()
  const assetsBunchStore = useAssetsBunchStore()
  const cashFlowProjectionsStore = useCashFlowProjectionsStore()
  const cashFlowProjectionsBunchStore = useCashFlowProjectionsBunchStore()
  const contactsStore = useContactsStore()
  const contactsBunchStore = useContactsBunchStore()
  const dashboardsStore = useDashboardsStore()
  const documentsStore = useDocumentsStore()
  const documentsTagsStore = useDocumentsTagsStore()
  const documentsBunchStore = useDocumentsBunchStore()
  const filterStorage = useFilterStore()
  const linkedDataStore = useLinkedDataStore()
  const linkedDataConnectorsStore = useLinkedDataConnectorsStore()
  const linkedDataTaggingRulesStore = useLinkedDataTaggingRulesStore()
  const linkedDataMappingRulesStore = useLinkedDataMappingRulesStore()
  const linkedDataTransactionsTagsStore = useLinkedDataTransactionsTagsStore()
  const modalsStore = useModalsStore()
  const repositoriesStore = useRepositoriesStore()
  const repositoriesCollaboratorsStore = useRepositoriesCollaboratorsStore()
  const tagsBunchStore = useTagsBunchStore()
  const tagsStore = useTagsStore()
  const tasksStore = useTasksStore()
  const transactionsStore = useTransactionsStore()
  const transactionsBunchStore = useTransactionsBunchStore()
  const transactionsDeletedStore = useTransactionsDeletedStore()
  const transactionsSettingsStore = useTransactionsSettingsStore()
  const userSettingsStore = useUserSettingsStore()

  const slideOverFormDirty = ref(false)
  const isAppInitialized = ref(false)

  const blockers = ref<Map<string, () => Promise<any>>>(new Map())

  // GETTERS
  const getAppInitialized = computed(() => isAppInitialized.value)

  // SETTERS
  const setAppInitialized = (value: boolean) => {
    isAppInitialized.value = value
  }

  // ACTIONS

  const addBlocker = (key: string, action: () => Promise<any>) => {
    blockers.value.set(key, action)
  }

  const removeBlocker = (key: string) => {
    blockers.value.delete(key)
  }

  const clearBlockers = () => {
    blockers.value.clear()
  }

  const checkBlocker = async (key: string) => new Promise(async (resolve, reject) => {
    const blocker = blockers.value.get(key)
    if (blocker) {
      try {
        await blocker()
        removeBlocker(key)
      } catch {
        reject(false)
      }
    }
    resolve(true)
  })

  const checkBlockers = () => new Promise(async (resolve, reject) => {
    if (blockers.value.size > 0) {
      for await (const [key, blocker] of blockers.value.entries()) {
        try {
          await blocker()
          removeBlocker(key)
        } catch {
          reject(false)
        }
      }
    }
    resolve(true)
  })

  const fetchRepositoryData = async () => {
    try {
      await Promise.all([
        repositoriesCollaboratorsStore.fetchInvites(),
        repositoriesStore.fetch(),
        repositoriesStore.fetchCurrencies(),
        assetsStore.fetchTypes(),
        userSettingsStore.fetch(),
      ])
      await repositoriesStore.defineCurrent()
    } catch (e) {
      handleCatchedError(e as string)
    }
  }

  const fetchCurrentRepositoryData = async () => {
    try {
      await Promise.all([
        assetsStore.fetch(),
        assetsDeletedStore.fetch(),
        contactsStore.fetch(),
        documentsStore.fetch(),
        documentsTagsStore.fetch(),
        tagsStore.fetch(),
        tasksStore.fetch(),
      ])
      if (!repositoriesStore.isPartiallyShared) {
        await Promise.all([
          linkedDataStore.fetch(),
          linkedDataConnectorsStore.fetch(),
          linkedDataTransactionsTagsStore.fetch()
        ])
      }
    } catch (e) {
      handleCatchedError(e as string)
    }
  }

  const cancelFetch = async () => {
    await analyticsStore.cancel()
    await assetsStore.cancel()
    await assetsDeletedStore.cancel()
    await assetPricesStore.cancel()
    await assetTaxLotsStore.cancel()
    await cashFlowProjectionsStore.cancel()
    await contactsStore.cancel()
    await documentsStore.cancel()
    await documentsTagsStore.cancel()
    await linkedDataStore.cancel()
    await linkedDataConnectorsStore.cancel()
    await linkedDataTaggingRulesStore.cancel()
    await linkedDataMappingRulesStore.cancel()
    await linkedDataTransactionsTagsStore.cancel()
    await tagsStore.cancel()
    await transactionsStore.cancel()
    await transactionsDeletedStore.cancel()
    await transactionsSettingsStore.cancel()
  }

  const clearState = async () => {
    clearBlockers()

    await analyticsStore.clear()
    await assetsStore.clear()
    await assetsDeletedStore.clear()
    await assetTaxLotsStore.clear()
    await assetsBunchStore.clear()
    await cashFlowProjectionsStore.clear()
    await cashFlowProjectionsBunchStore.clear()
    await contactsStore.clear()
    await contactsBunchStore.clear()
    await dashboardsStore.clear()
    await documentsStore.clear()
    await documentsTagsStore.clear()
    await documentsBunchStore.clear()
    await filterStorage.clear()
    await linkedDataStore.clear()
    await linkedDataConnectorsStore.clear()
    await linkedDataTaggingRulesStore.clear()
    await linkedDataMappingRulesStore.clear()
    await linkedDataTransactionsTagsStore.clear()
    await modalsStore.clear()
    await tagsStore.clear()
    await tagsBunchStore.clear()
    await tasksStore.clear()
    await transactionsStore.clear()
    await transactionsBunchStore.clear()
    await transactionsDeletedStore.clear()
  }

  const globalClear = async () => {
    await clearState()
    await modalsStore.clear([])
    await repositoriesStore.clear()
    await repositoriesCollaboratorsStore.clear()
    await transactionsSettingsStore.clear()

    // clear LocalStorage except for color scheme
    Object.keys(localStorage).forEach(key => {
      if (key !== 'vueuse-color-scheme') {
        localStorage.removeItem(key)
      }
    })
    sessionStorage.clear()
  }

  return {
    getAppInitialized,
    checkBlocker,
    checkBlockers,

    setAppInitialized,

    addBlocker,
    removeBlocker,

    slideOverFormDirty,
    fetchRepositoryData,
    fetchCurrentRepositoryData,
    cancelFetch,
    clearState,
    globalClear,
  }
})
